import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {ListEmptyState} from '@/components/section-card'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {CLASIFICACION_TONE, DESCARTES_PAGE_SIZE, useDescartes, type Descarte} from '@/hooks/sgsst/riesgos/use-ipevr'
import {formatDate} from '@/lib/risk'

function DescarteCard({descarte}: {descarte: Descarte}) {
  const {t} = useTranslation()
  return (
    <li className="space-y-2 px-4 py-3.5 transition-colors hover:bg-secondary/30" data-testid="ipevr-descarte">
      <div className="flex flex-wrap items-center gap-2">
        <Tag tone={CLASIFICACION_TONE[descarte.fila_clasificacion]}>
          {t(`ipevr.clasificacion.${descarte.fila_clasificacion}`)}
        </Tag>
        <span className="text-sm text-foreground">{descarte.fila_descripcion}</span>
        <Tag tone="neutral" data-testid="ipevr-descarte-ambito">
          {descarte.ambito === 'fila' ? t('ipevr.descarte.ambitoFila') : t('ipevr.descarte.ambitoProcedencia')}
        </Tag>
      </div>

      {descarte.procedencias.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="ipevr-descarte-personas">
          {t('ipevr.descarte.personas', {
            names: descarte.procedencias.map((p) => `${p.employee_name} ${p.employee_last_name}`).join(', '),
          })}
        </p>
      )}

      <div className="space-y-1">
        <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('ipevr.descarte.motivoLabel')}
        </p>
        <p className="text-sm text-foreground" data-testid="ipevr-descarte-motivo">
          {descarte.motivo}
        </p>
      </div>

      <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
        {descarte.descartado_por
          ? t('ipevr.descarte.porYCuando', {
              name: `${descarte.descartado_por.name} ${descarte.descartado_por.last_name}`,
              fecha: formatDate(descarte.descartado_en),
            })
          : t('ipevr.descarte.cuando', {fecha: formatDate(descarte.descartado_en)})}
      </p>
    </li>
  )
}

export function DescartesTab({businessId, active}: {businessId: string; active: boolean}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const {data: sobre, isLoading} = useDescartes(businessId, active, page)
  const descartes = sobre?.items

  if (isLoading) return <Skeleton className="h-24 w-full" data-testid="ipevr-descartes-loading" />

  const vacio = !sobre || sobre.total === 0

  return (
    <section className="surface overflow-hidden">
      <div className="surface-divider flex items-center gap-1 border-b px-4 py-3">
        <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('ipevr.tabs.descartados')}
        </span>
        <HelpIcon label={t('ipevr.tipLabel.descartes')} testId="ipevr-descartes-help">
          {t('ipevr.descarte.tip')}
        </HelpIcon>
      </div>
      {vacio ? (
        <div data-testid="ipevr-descartes-empty">
          <ListEmptyState title={t('ipevr.descarte.empty')} />
        </div>
      ) : (
        <>
          <ul className="divide-y divide-border/60" data-testid="ipevr-descartes-list">
            {(descartes ?? []).map((descarte) => (
              <DescarteCard key={descarte.id} descarte={descarte} />
            ))}
          </ul>
          <div className="px-4 py-2">
            <PaginationBar page={page} pageSize={DESCARTES_PAGE_SIZE} total={sobre.total} onPageChange={setPage} />
          </div>
        </>
      )}
    </section>
  )
}
